import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
} from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { menuItems, menuLabels } from './app.navigation';

import { toSignal } from '@angular/core/rxjs-interop';
import { SanctumAuthService } from '@freesixtyfive/auth';
import { SidebarLayoutComponent } from '@fsf/ui';
import { MenuItem } from 'primeng/api';
import { map } from 'rxjs';
import { UserService } from './shared/services/user.service';
import { links } from './theme-preset';

@Component({
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    ButtonModule,
    TooltipModule,
    SidebarLayoutComponent,
  ],
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  #router = inject(Router);
  #meta = inject(Meta);
  #translate = inject(TranslateService);
  #auth = inject(SanctumAuthService);
  #user = inject(UserService);

  isAuthenticated = this.#auth.isAuth;
  links = links;
  #menuItems = toSignal(
    this.#translate
      .stream(menuLabels)
      .pipe(
        map((translations) =>
          menuItems.map((item) => this.translateMenuItem(item, translations)),
        ),
      ),
    { initialValue: [] },
  );

  items = computed(() => {
    if (!this.#auth.isAuth()) return [];
    const perms = this.#auth.permissions();
    return this.filterByPermission(this.#menuItems() ?? [], perms);
  });

  /** Surfaces the Tagesdashboard quick-link button in the top toolbar
   *  whenever the user can use the cash register. */
  canUseKasse = computed(
    () => this.#auth.isAuth() && this.#auth.permissions().includes('kasse.use'),
  );

  constructor() {}

  ngOnInit(): void {}

  logout(e?: any): void {
    this.#auth.signOut().subscribe(() => {
      this.#user.clearUser();
      this.#router.navigate(['/login']);
    });
  }

  private filterByPermission(items: MenuItem[], perms: string[]): MenuItem[] {
    return items
      .filter((item: any) => !item.permission || perms.includes(item.permission))
      .map((item) => ({
        ...item,
        items: item.items?.length
          ? this.filterByPermission(item.items, perms)
          : item.items,
      }))
      .filter((item) => !item.items || item.items.length > 0);
  }

  private translateMenuItem(item: MenuItem, translations: any): MenuItem {
    const translatedItem: MenuItem = {
      ...item,
      label: translations[item.label as string] || item.label,
    };

    if (item.items && item.items.length > 0) {
      translatedItem.items = item.items.map((subItem) =>
        this.translateMenuItem(subItem, translations),
      );
    }

    return translatedItem;
  }
}
